import { useEffect, useState } from 'react'
import Drawer from '@mui/material/Drawer'
import { useTranslation } from 'react-i18next'
import { useNavigate, useLocation } from 'react-router-dom'
import { WEBSITE_BUILDER_ENABLED } from '@/features/website-builder/config'

import { logout } from '@/features/auth/services/authService'
import { useCurrentAccount } from '@/features/auth/hooks/useCurrentAccount'
import { useOnboardingTour } from '@/features/onboarding'
import { useSettingsModal } from '@/features/settings'
import styles from './SideBar.module.scss'
import sideBarStyles from './SideBar.styles'
import Button from '../Button/Button'
import AccountButton from '../AccountButton/AccountButton'
import AccountMenu from './AccountMenu'
import SideBarChevron from './SideBarChevron'
import HelpModal from '../Modal/HelpModal/HelpModal'

const ACCOUNT_MENU_ID = 'account-menu'

const pathToOptionMap: { [key: string]: string } = {
  '/dashboard': 'Dashboard',
  '/table-cards-management': 'TableCards',
  '/loyalty': 'Loyalty',
  '/floor-plan': 'FloorPlan',
  '/mobile-app-management': 'MobileAppManagement',
  '/website/template': 'WebsiteTemplate',
  '/website/builder': 'WebsiteBuilder',
  '/website': 'Website',
  '/feedback': 'Feedback',
  '/employees-management': 'Employees',
  '/employees-schedule': 'EmployeesSchedule',
  '/menu-management/dishes': 'Dishes',
  '/menu-management/toppings': 'Toppings',
  '/menu-management/ingredients': 'Ingredients',
  '/menu-management/data-extraction': 'DataExtraction',
  '/register-device': 'RegisterDevice',
  '/sales-reports/menu-items': 'SalesMenuItems',
  '/sales-reports/inventory': 'SalesInventory',
  '/sales-reports': 'SalesReports'
}

function matchOptionForPath(pathname: string) {
  const entry = Object.entries(pathToOptionMap).find(
    ([key]) => pathname === key || pathname.startsWith(`${key}/`)
  )
  return entry?.[1] ?? 'Dashboard'
}

type SubMenu = 'Menu' | 'SalesReports' | 'Website'

function SideBar() {
  const [menuOptionSelected, setMenuOptionSelected] = useState('Dashboard')
  const [isOpen, setIsOpen] = useState(false)
  const [isPinned, setIsPinned] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isLockconfirmButtonDisabled, setIsLockconfirmButtonDisabled] =
    useState(false)
  // One value rather than a flag per submenu: only one may be open at a time,
  // so opening one has to close whichever was open before.
  const [openSubMenu, setOpenSubMenu] = useState<SubMenu | null>(null)
  const isMenuPageOpen = openSubMenu === 'Menu'
  const isSalesReportsOpen = openSubMenu === 'SalesReports'
  const isWebsiteOpen = openSubMenu === 'Website'
  const [accountMenuAnchor, setAccountMenuAnchor] =
    useState<HTMLElement | null>(null)
  const account = useCurrentAccount()
  const openSettings = useSettingsModal(s => s.open)
  const [renderExpanded, setRenderExpanded] = useState(false)

  // Replaying is the provider's call: it picks the tour for the current page and
  // clears whatever progress would otherwise keep that tour quiet.
  const startOnboardingTour = useOnboardingTour(s => s.start)

  const getLockButtonLabel = () => {
    if (!renderExpanded) return ''
    return isPinned ? t('SIDEBAR.UNPIN') : t('SIDEBAR.PIN')
  }

  const getSideBarWidth = () => {
    if (!isPinned) return isOpen ? '165px' : '90px'
    return '165px'
  }

  const handleOpenModal = () => {
    setIsPinned(true)
    setIsLockconfirmButtonDisabled(true)
    setIsModalOpen(true)
  }

  const { t } = useTranslation()

  const navigate = useNavigate()

  const location = useLocation()

  useEffect(() => {
    let timeoutId: ReturnType<typeof window.setTimeout>

    if (isOpen && !isPinned) {
      timeoutId = setTimeout(() => {
        setRenderExpanded(true)
      }, 200)
    } else if (!isOpen && !isPinned) {
      timeoutId = setTimeout(() => {
        setRenderExpanded(false)
      }, 75)
    }

    return () => clearTimeout(timeoutId)
  }, [isOpen, isPinned])

  const handleNavigationClick = (url: string) => {
    navigate(url)
  }

  const closeAccountMenu = () => {
    setAccountMenuAnchor(null)
    // Closing by a click outside the sidebar sends it no mouseleave, as the
    // pointer was over the menu's backdrop. Ask where the pointer is instead.
    setIsOpen(!!document.getElementById('tour-sidebar')?.matches(':hover'))
  }

  const toggleSubMenu = (subMenu: SubMenu, url: string) => {
    const nextOpen = openSubMenu !== subMenu
    setOpenSubMenu(nextOpen ? subMenu : null)
    if (nextOpen) {
      navigate(url)
    }
  }

  const handleMenuPageClick = () =>
    toggleSubMenu('Menu', '/menu-management/dishes')

  const handleSalesReportsClick = () =>
    toggleSubMenu('SalesReports', '/sales-reports/menu-items')

  const handleWebsiteClick = () => {
    // One page and no submenu while the builder is off: a submenu with a
    // single entry is a step that leads nowhere.
    if (!WEBSITE_BUILDER_ENABLED) {
      navigate('/website/template')
      return
    }
    toggleSubMenu('Website', '/website/template')
  }

  const isWebsiteOption = (option: string) =>
    option === 'Website' ||
    option === 'WebsiteTemplate' ||
    option === 'WebsiteBuilder'

  const isMenuOption = (option: string) =>
    option === 'Menu' ||
    option === 'Dishes' ||
    option === 'Toppings' ||
    option === 'Ingredients' ||
    option === 'DataExtraction'

  const isSalesOption = (option: string) =>
    option === 'SalesReports' ||
    option === 'SalesMenuItems' ||
    option === 'SalesInventory'

  useEffect(() => {
    const option = matchOptionForPath(location.pathname)
    setMenuOptionSelected(option)
    if (isMenuOption(option)) {
      setOpenSubMenu('Menu')
    }
    if (isSalesOption(option)) {
      setOpenSubMenu('SalesReports')
    }
    // With the builder off WWW is a plain page, and landing on one must not
    // fold away a submenu the user left open.
    if (isWebsiteOption(option) && WEBSITE_BUILDER_ENABLED) {
      setOpenSubMenu('Website')
    }
  }, [location.pathname])

  const isMenuActive = isMenuOption(menuOptionSelected)
  const isSalesActive = isSalesOption(menuOptionSelected)
  const isWebsiteActive = isWebsiteOption(menuOptionSelected)

  return (
    <>
      <Drawer
        variant="permanent"
        anchor="left"
        slotProps={{ paper: { id: 'tour-sidebar' } }}
        sx={{
          ...sideBarStyles,
          '& .MuiDrawer-paper': {
            ...sideBarStyles['& .MuiDrawer-paper'],
            width: getSideBarWidth()
          }
        }}
        onMouseOver={() => setIsOpen(true)}
        onMouseLeave={() => setIsOpen(false)}
      >
        <div className={`${styles.sideBarTop}`}>
          {renderExpanded ? (
            <div className={`${styles.sideBarLogoContainer}`}>
              <p className={`logo-font ${styles.sideBarLogo}`}>
                <mark className="mark-white">MENU</mark>
                <mark className="mark-orange">!</mark>
              </p>
              <p className={`logo-font-small ${styles.sideBarLogo}`}>
                <mark className="mark-orange">RMS</mark>
              </p>
            </div>
          ) : (
            <div className={`${styles.sideBarLogoContainer}`}>
              <p className={`logo-font ${styles.sideBarLogo}`}>
                <mark className="mark-white">M</mark>
                <mark className="mark-orange">!</mark>
              </p>
              <p className={`logo-font-small ${styles.sideBarLogo}`}>
                <mark className="mark-orange">RMS</mark>
              </p>
            </div>
          )}
        </div>
        <div className={`${styles.sideBarContent}`}>
          <div className={`${styles.sideBarMenu}`}>
            <div
              id="tour-sidebar-lock"
              className={`${styles.sideBarLockButton}`}
            >
              <Button
                onClick={() => setIsPinned(!isPinned)}
                label={getLockButtonLabel()}
                ariaLabel={isPinned ? t('SIDEBAR.UNPIN') : t('SIDEBAR.PIN')}
                variant="text"
                color={isPinned ? 'secondary' : 'primary'}
                textAlignment="left"
                icon={isPinned ? 'las la-lock' : 'las la-lock-open'}
                disabled={isLockconfirmButtonDisabled}
                condensedLabel
              />
            </div>
            <Button
              onClick={() => handleNavigationClick('/dashboard')}
              label={renderExpanded ? t('SIDEBAR.MENU.DASHBOARD') : ''}
              ariaLabel={t('SIDEBAR.MENU.DASHBOARD')}
              variant="text"
              color={
                menuOptionSelected === 'Dashboard' ? 'secondary' : 'primary'
              }
              textAlignment="left"
              icon="las la-chart-pie"
              condensedLabel
            />
            <Button
              onClick={() => handleNavigationClick('/table-cards-management')}
              label={renderExpanded ? t('SIDEBAR.MENU.TABLE_CARDS') : ''}
              ariaLabel={t('SIDEBAR.MENU.TABLE_CARDS')}
              variant="text"
              color={
                menuOptionSelected === 'TableCards' ? 'secondary' : 'primary'
              }
              textAlignment="left"
              icon="las la-qrcode"
              condensedLabel
            />
            <Button
              onClick={() => handleNavigationClick('/loyalty')}
              label={renderExpanded ? t('SIDEBAR.MENU.LOYALTY') : ''}
              ariaLabel={t('SIDEBAR.MENU.LOYALTY')}
              variant="text"
              color={menuOptionSelected === 'Loyalty' ? 'secondary' : 'primary'}
              textAlignment="left"
              icon="las la-gift"
              condensedLabel
            />
            <Button
              onClick={() => handleNavigationClick('/floor-plan')}
              label={renderExpanded ? t('SIDEBAR.MENU.FLOOR_PLAN') : ''}
              ariaLabel={t('SIDEBAR.MENU.FLOOR_PLAN')}
              variant="text"
              color={
                menuOptionSelected === 'FloorPlan' ? 'secondary' : 'primary'
              }
              textAlignment="left"
              icon="las la-store-alt"
              condensedLabel
            />
            <Button
              onClick={() => handleNavigationClick('/mobile-app-management')}
              label={renderExpanded ? t('SIDEBAR.MENU.MOBILE_APP') : ''}
              ariaLabel={t('SIDEBAR.MENU.MOBILE_APP')}
              variant="text"
              color={
                menuOptionSelected === 'MobileAppManagement'
                  ? 'secondary'
                  : 'primary'
              }
              textAlignment="left"
              icon="las la-mobile"
              condensedLabel
            />
            <div
              className={`${styles.menuButtonWrapper} ${
                isWebsiteActive
                  ? styles.menuButtonActive
                  : styles.menuButtonInactive
              }`}
            >
              <Button
                onClick={handleWebsiteClick}
                label={renderExpanded ? t('SIDEBAR.MENU.WEBSITE') : ''}
                ariaLabel={t('SIDEBAR.MENU.WEBSITE')}
                variant="text"
                color={isWebsiteActive ? 'secondary' : 'primary'}
                textAlignment="left"
                icon="las la-globe"
                condensedLabel
              />
              {renderExpanded && WEBSITE_BUILDER_ENABLED && (
                <SideBarChevron
                  highlighted={isWebsiteOpen}
                  onClick={handleWebsiteClick}
                  label={t('SIDEBAR.MENU.WEBSITE')}
                />
              )}
            </div>
            {renderExpanded && WEBSITE_BUILDER_ENABLED && isWebsiteOpen && (
              <div className={`${styles.sideBarSubMenu}`}>
                <Button
                  onClick={() => handleNavigationClick('/website/template')}
                  label={t('SIDEBAR.WEBSITE.TEMPLATE')}
                  variant="text"
                  color={
                    menuOptionSelected === 'WebsiteTemplate'
                      ? 'secondary'
                      : 'primary'
                  }
                  textAlignment="left"
                  smallFont
                  secondaryButton
                  condensedLabel
                />
                <Button
                  onClick={() => handleNavigationClick('/website/builder')}
                  label={t('SIDEBAR.WEBSITE.BUILDER')}
                  variant="text"
                  color={
                    menuOptionSelected === 'WebsiteBuilder'
                      ? 'secondary'
                      : 'primary'
                  }
                  textAlignment="left"
                  smallFont
                  secondaryButton
                  condensedLabel
                />
              </div>
            )}
            <Button
              onClick={() => handleNavigationClick('/feedback')}
              label={renderExpanded ? t('SIDEBAR.MENU.FEEDBACK') : ''}
              ariaLabel={t('SIDEBAR.MENU.FEEDBACK')}
              variant="text"
              color={
                menuOptionSelected === 'Feedback' ? 'secondary' : 'primary'
              }
              textAlignment="left"
              icon="las la-comment"
              condensedLabel
            />
            <div
              className={`${styles.menuButtonWrapper} ${
                isSalesActive
                  ? styles.menuButtonActive
                  : styles.menuButtonInactive
              }`}
            >
              <Button
                onClick={handleSalesReportsClick}
                label={renderExpanded ? t('SIDEBAR.MENU.SALES_REPORTS') : ''}
                ariaLabel={t('SIDEBAR.MENU.SALES_REPORTS')}
                variant="text"
                color={isSalesActive ? 'secondary' : 'primary'}
                textAlignment="left"
                icon="las la-file-invoice-dollar"
                condensedLabel
              />
              {renderExpanded && (
                <SideBarChevron
                  highlighted={isSalesReportsOpen}
                  onClick={handleSalesReportsClick}
                  label={t('SIDEBAR.MENU.SALES_REPORTS')}
                />
              )}
            </div>
            {renderExpanded && isSalesReportsOpen && (
              <div className={`${styles.sideBarSubMenu}`}>
                <Button
                  onClick={() =>
                    handleNavigationClick('/sales-reports/menu-items')
                  }
                  label={t('SIDEBAR.SALES_REPORTS.MENU_ITEMS')}
                  variant="text"
                  color={
                    menuOptionSelected === 'SalesMenuItems'
                      ? 'secondary'
                      : 'primary'
                  }
                  textAlignment="left"
                  smallFont
                  secondaryButton
                  condensedLabel
                />
                <Button
                  onClick={() =>
                    handleNavigationClick('/sales-reports/inventory')
                  }
                  label={t('SIDEBAR.SALES_REPORTS.INVENTORY')}
                  variant="text"
                  color={
                    menuOptionSelected === 'SalesInventory'
                      ? 'secondary'
                      : 'primary'
                  }
                  textAlignment="left"
                  smallFont
                  secondaryButton
                  condensedLabel
                />
              </div>
            )}
            <Button
              onClick={() => handleNavigationClick('/employees-management')}
              label={renderExpanded ? t('SIDEBAR.MENU.EMPLOYEES') : ''}
              ariaLabel={t('SIDEBAR.MENU.EMPLOYEES')}
              variant="text"
              color={
                menuOptionSelected === 'Employees' ? 'secondary' : 'primary'
              }
              textAlignment="left"
              icon="las la-users-cog"
              condensedLabel
            />
            <Button
              onClick={() => handleNavigationClick('/employees-schedule')}
              label={renderExpanded ? t('SIDEBAR.MENU.SCHEDULE') : ''}
              ariaLabel={t('SIDEBAR.MENU.SCHEDULE')}
              variant="text"
              color={
                menuOptionSelected === 'EmployeesSchedule'
                  ? 'secondary'
                  : 'primary'
              }
              textAlignment="left"
              icon="las la-calendar-alt"
              condensedLabel
            />
            <Button
              onClick={() => handleNavigationClick('/register-device')}
              label={renderExpanded ? t('SIDEBAR.MENU.REGISTER_DEVICE') : ''}
              ariaLabel={t('SIDEBAR.MENU.REGISTER_DEVICE')}
              variant="text"
              color={
                menuOptionSelected === 'RegisterDevice'
                  ? 'secondary'
                  : 'primary'
              }
              textAlignment="left"
              icon="las la-tablet"
              condensedLabel
            />
            <div
              className={`${styles.menuButtonWrapper} ${
                isMenuActive
                  ? styles.menuButtonActive
                  : styles.menuButtonInactive
              }`}
            >
              <Button
                onClick={handleMenuPageClick}
                label={renderExpanded ? t('SIDEBAR.MENU') : ''}
                ariaLabel={t('SIDEBAR.MENU')}
                variant="text"
                color={isMenuActive ? 'secondary' : 'primary'}
                textAlignment="left"
                icon="las la-clipboard-list"
                condensedLabel
              />
              {renderExpanded && (
                <SideBarChevron
                  highlighted={isMenuPageOpen}
                  onClick={handleMenuPageClick}
                  label={t('SIDEBAR.MENU')}
                />
              )}
            </div>
            {renderExpanded && isMenuPageOpen && (
              <div className={`${styles.sideBarSubMenu}`}>
                <Button
                  onClick={() =>
                    handleNavigationClick('/menu-management/dishes')
                  }
                  label={t('SIDEBAR.MENU.DISHES')}
                  variant="text"
                  color={
                    menuOptionSelected === 'Dishes' ||
                    menuOptionSelected === 'Menu'
                      ? 'secondary'
                      : 'primary'
                  }
                  textAlignment="left"
                  smallFont
                  secondaryButton
                  condensedLabel
                />
                <Button
                  onClick={() =>
                    handleNavigationClick('/menu-management/toppings')
                  }
                  label={t('SIDEBAR.MENU.TOPPINGS')}
                  variant="text"
                  color={
                    menuOptionSelected === 'Toppings' ? 'secondary' : 'primary'
                  }
                  textAlignment="left"
                  smallFont
                  secondaryButton
                  condensedLabel
                />
                <Button
                  onClick={() =>
                    handleNavigationClick('/menu-management/ingredients')
                  }
                  label={t('SIDEBAR.MENU.INGREDIENTS')}
                  variant="text"
                  color={
                    menuOptionSelected === 'Ingredients'
                      ? 'secondary'
                      : 'primary'
                  }
                  textAlignment="left"
                  smallFont
                  secondaryButton
                  condensedLabel
                />
                <Button
                  onClick={() =>
                    handleNavigationClick('/menu-management/data-extraction')
                  }
                  label={t('SIDEBAR.MENU.DATA_EXTRACTION')}
                  variant="text"
                  color={
                    menuOptionSelected === 'DataExtraction'
                      ? 'secondary'
                      : 'primary'
                  }
                  textAlignment="left"
                  smallFont
                  secondaryButton
                  condensedLabel
                />
              </div>
            )}
          </div>

          <div className={styles.sideBarAccount} id="tour-account-button">
            <AccountButton
              name={account.name}
              photoUrl={account.photoUrl}
              collapsed={!renderExpanded}
              active={!!accountMenuAnchor}
              ariaLabel={t('SIDEBAR.ACCOUNT')}
              menuId={ACCOUNT_MENU_ID}
              onClick={event => setAccountMenuAnchor(event.currentTarget)}
            />
            <AccountMenu
              id={ACCOUNT_MENU_ID}
              anchorEl={accountMenuAnchor}
              restaurantName={account.restaurantName}
              onClose={closeAccountMenu}
              onSettings={() => openSettings()}
              onHelp={handleOpenModal}
              onTour={startOnboardingTour}
              onLogout={() => logout().then(() => navigate('/login'))}
            />
          </div>
        </div>
      </Drawer>
      <HelpModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false)
          setIsLockconfirmButtonDisabled(false)
        }}
      />
    </>
  )
}

export default SideBar
